<script setup lang="ts">
import { isEqual } from 'ohash'
import { computed } from 'vue'
import type { RouteLocationNormalizedLoaded, RouteLocationRaw } from 'vue-router'

type RouteLike = Pick<RouteLocationNormalizedLoaded, 'query' | 'hash'>

const currentRoute = useRoute()

const props = withDefaults(defineProps<{
  to?: RouteLocationRaw | null
  href?: string | null
  target?: string | null
  rel?: string | null
  as?: string
  disabled?: boolean
  active?: boolean
  exact?: boolean
  exactQuery?: boolean
  exactHash?: boolean
  activeClass?: string
  inactiveClass?: string
}>(), {
  to: null,
  href: null,
  target: null,
  rel: null,
  as: 'button',
  disabled: false,
  active: false,
  exact: false,
  exactQuery: false,
  exactHash: false,
  activeClass: undefined,
  inactiveClass: undefined,
})

const to = computed(() => props.to ?? undefined)

function resolveLinkClass(
  route: RouteLike,
  currentRoute: RouteLike,
  {
    isActive,
    isExactActive,
  }: { isActive: boolean; isExactActive: boolean },
): string | undefined {
  if (props.exactQuery && !isEqual(route.query, currentRoute.query)) {
    return props.inactiveClass
  }
  if (props.exactHash && route.hash !== currentRoute.hash) {
    return props.inactiveClass
  }

  if (props.exact && isExactActive) {
    return props.activeClass
  }

  if (!props.exact && isActive) {
    return props.activeClass
  }

  return props.inactiveClass
}
</script>

<template>
  <component
    :is="as"
    v-if="!to"
    :disabled="disabled || undefined"
    :href="href ?? undefined"
    :rel="rel ?? undefined"
    :target="target ?? undefined"
    v-bind="$attrs"
    :class="active ? activeClass : inactiveClass"
  >
    <slot />
  </component>

  <NuxtLink
    v-else
    v-slot="{
      route,
      href: slotHref,
      navigate,
      isActive,
      isExactActive,
    }"
    :to="to"
    custom
  >
    <a
      v-bind="$attrs"
      :href="!disabled ? (slotHref ?? undefined) : undefined"
      :aria-disabled="disabled ? 'true' : undefined"
      :role="disabled ? 'link' : undefined"
      :rel="rel ?? undefined"
      :target="target ?? undefined"
      :class="
        active
          ? activeClass
          : resolveLinkClass(route ?? currentRoute, currentRoute, {
            isActive,
            isExactActive,
          })
      "
      @click="(e) => {
        if (disabled) {
          e.preventDefault()
          return
        }
        navigate(e)
      }"
    >
      <slot v-bind="{ isActive: exact ? isExactActive : isActive }" />
    </a>
  </NuxtLink>
</template>
